/**
 * React-free shared helpers — the single source of truth for locale and
 * environment logic.
 *
 * Everything here is pure and safe to import from the Express server,
 * Vike config, build scripts and React code alike. React-dependent wrappers
 * (hooks) live elsewhere: `useLocale` in `@/lib/locale`, `useEnvironment` in
 * `@/hooks/use-environment`.
 */

const SUPPORTED_LOCALES = ['en', 'sv'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export function parseAcceptLanguage(header?: string): Locale {
  if (!header) return 'en';

  const locales = header
    .split(',')
    .map((part) => {
      const [tag, qPart] = part.trim().split(';');
      const q = qPart ? parseFloat(qPart.trim().replace('q=', '')) : 1;
      return { tag: tag.trim(), q: Number.isNaN(q) ? 0 : q };
    })
    .sort((a, b) => b.q - a.q);

  for (const { tag } of locales) {
    const lower = tag.toLowerCase();
    if (lower.startsWith('sv')) return 'sv';
    if (lower.startsWith('en')) return 'en';
  }

  return 'en';
}

export const OG_LOCALE_MAP: Record<string, string> = {
  en: 'en_GB',
  sv: 'sv_SE',
};

export function localizePath(path: string, locale: Locale): string {
  // Pass-through for external URLs, mailto, anchors, and static assets
  if (path.startsWith('http') || path.startsWith('mailto:')) return path;
  if (path.startsWith('#')) return path;
  if (path.startsWith('/logos/') || path.startsWith('/assets/')) return path;

  // Already localized (matches /en or /sv at the start of the path)
  if (path.match(/^\/(en|sv)(\/|$)/)) return path;

  // Root path
  if (path === '/') return `/${locale}/`;

  return `/${locale}${path}`;
}

export function switchLocalePath(currentPath: string, newLocale: Locale): string {
  const replaced = currentPath.replace(/^\/(en|sv)(\/|$)/, `/${newLocale}$2`);
  if (!replaced.startsWith(`/${newLocale}`)) {
    return `/${newLocale}${replaced.startsWith('/') ? replaced : '/' + replaced}`;
  }
  // Ensure root locale paths have a trailing slash
  if (replaced === `/${newLocale}`) return `/${newLocale}/`;
  return replaced || `/${newLocale}/`;
}

/** Single source of truth for which backend a hostname should call:
 *  stage/localhost → stage API, everything else → prod. Runtime — no build
 *  args, so every environment (including local dev) gets the right URL.
 */
export function isStageHostname(hostname: string): boolean {
  return hostname === 'stage.berget.ai' || hostname === 'localhost' || hostname === '127.0.0.1';
}

export function apiUrlForHostname(hostname: string): string {
  return isStageHostname(hostname) ? 'https://api.stage.berget.ai' : 'https://api.berget.ai';
}

export function getCookieValue(cookieHeader: string | undefined, name: string): string | undefined {
  if (!cookieHeader) return undefined;
  const match = cookieHeader.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}
